'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const sections = [
  {
    id: 'how-it-works',
    title: 'How It Works',
    content: [
      {
        title: 'AI-Powered Attractiveness Test Analysis',
        description: 'Get an instant AI Attractiveness Test for your photos Online. Our advanced AI analyzes facial features for Free, providing an attractiveness Face Rating, FaceAnalyzer, and BeautyScore.',
        points: [
          'Facial landmark detection',
          'Feature symmetry analysis',
          'Golden ratio calculations',
          'Skin texture evaluation',
        ],
      },
      {
        title: 'Scientific Basis',
        description: 'Our rating system is based on extensive research in facial aesthetics, psychology, and human perception.',
        points: [
          'Facial symmetry studies',
          'Cross-cultural beauty standards',
          'Psychological research',
          'Modern beauty trends',
        ],
      },
    ],
  },
  {
    id: 'features',
    title: 'Rating Features',
    content: [
      {
        title: 'Comprehensive Analysis',
        description: 'We analyze multiple aspects of facial features to provide a detailed beauty score.',
        points: [
          'Facial symmetry and proportion',
          'Skin quality and texture',
          'Feature harmony and balance',
          'Age and youth indicators',
        ],
      },
      {
        title: 'Personalized Insights',
        description: 'Receive detailed feedback and personalized recommendations.',
        points: [
          'Detailed score breakdown',
          'Feature-specific analysis',
          'Improvement suggestions',
          'Celebrity comparisons',
        ],
      },
    ],
  },
  {
    id: 'tips',
    title: 'Photo Tips',
    content: [
      {
        title: 'Best Photos for AI Attractiveness Test',
        description: 'Follow these tips to capture your best features.',
        points: [
          'Find your best angle',
          'Use proper lighting',
          'Choose the right background',
          'Consider timing and preparation',
        ],
      },
      {
        title: 'Technical Requirements',
        description: 'Ensure your photos meet these criteria for the best results.',
        points: [
          'Good lighting conditions',
          'Clear, focused image',
          'Proper face angle',
          'Neutral expression',
        ],
      },
    ],
  },
];

const articles = [
  {
    title: 'The Science Behind Facial Beauty',
    excerpt: 'Explore the scientific research behind facial beauty and attractiveness...',
    image: '/images/science-of-beauty.jpg',
    link: '/articles/science-of-beauty',
  },
  {
    title: 'Perfect Profile Picture Guide',
    excerpt: 'How beauty standards have evolved throughout history and across cultures...',
    image: '/images/beauty-standards.jpg',
    link: '/articles/beauty-standards',
  },
  {
    title: 'Understanding AI Attractiveness Test Standards',
    excerpt: 'Understanding the psychological factors that influence perceived attractiveness...',
    image: '/images/psychology.jpg',
    link: '/articles/psychology',
  },
];

export default function LearnMoreContent() {
  const [activeSection, setActiveSection] = useState('how-it-works');

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-violet-900 text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-900 to-violet-600 opacity-90" />
        </div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Understanding Photo Rating
          </h1>
          <p className="mt-6 text-xl max-w-3xl">
            Discover how our AI technology analyzes photos to provide accurate beauty scores,
            and learn tips for capturing your best features.
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex space-x-8">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    activeSection === section.id
                      ? 'border-violet-500 text-violet-900'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {sections.map((section) => (
          <section
            key={section.id}
            className={`space-y-12 ${
              activeSection === section.id ? 'block' : 'hidden'
            }`}
          >
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              {section.title}
            </h2>
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              {section.content.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="px-6 py-8">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {item.title}
                    </h3>
                    <p className="mt-4 text-gray-600">{item.description}</p>
                    <ul className="mt-6 space-y-4">
                      {item.points.map((point, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="flex-shrink-0 h-6 w-6 text-violet-500">
                            <svg
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </span>
                          <span className="ml-3 text-gray-600">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Related Articles */}
        <section className="mt-24">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-12">
            Related Articles
          </h2>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            {articles.map((article, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105"
              >
                <div className="relative h-48">
                  <Image
                    src={article.image}
                    alt={article.title}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{article.excerpt}</p>
                  <Link
                    href={article.link}
                    className="text-violet-600 hover:text-violet-700 font-medium"
                  >
                    Read more →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="mt-24 bg-violet-900 rounded-2xl text-white">
          <div className="px-6 py-12 sm:px-12 sm:py-16 lg:py-20 lg:px-16">
            <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
              Ready to Try It Out?
            </h2>
            <p className="mt-4 text-lg">
              Upload your photo now and get a detailed analysis of your features.
            </p>
            <Link
              href="/"
              className="mt-8 inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-violet-900 bg-white hover:bg-gray-50 transition-colors duration-300"
            >
              Try Now
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-200 pt-8 text-center">
            <p className="text-base text-gray-400">
              &copy; {new Date().getFullYear()} AI Beauty Score. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
